import { useCallback, useEffect, useRef, useState } from "react";
import { api, ApiRequestError, tokenStore } from "../../api/client";
import { Icon } from "../Icon";

/**
 * FR-DASH-07 — one-click board-ready export of the current view.
 *
 * The job is async (rendering is measured in seconds), so the button owns the
 * poll. A failed render is shown as a failed render with its reason — the
 * failure mode this guards against is a spinner that stops and a file that
 * never appears, leaving the user unsure whether to wait or retry.
 */

interface ExportStatus {
  exportId: string;
  status: "queued" | "rendering" | "ready" | "failed";
  fileName: string | null;
  error: string | null;
  downloadUrl: string | null;
}

export function ExportButton({
  brandId,
  filters,
}: {
  brandId: string | null;
  filters: { days: number; pillarId: string; engine: string; persona: string };
}) {
  const [state, setState] = useState<ExportStatus | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
    },
    [],
  );

  const poll = useCallback(async (exportId: string, attempt = 0) => {
    try {
      const status = await api<ExportStatus>(`/exports/${exportId}`);
      setState(status);
      if (status.status === "ready") {
        setBusy(false);
        await download(status);
        return;
      }
      if (status.status === "failed") {
        setBusy(false);
        setError(status.error ?? "The export could not be generated.");
        return;
      }
      // NFR.md §1 budgets 60s for a standard export; give up a little past
      // that rather than polling forever with no explanation.
      if (attempt > 70) {
        setBusy(false);
        setError(
          "The export is taking longer than expected. Check the Reports page shortly.",
        );
        return;
      }
      timer.current = setTimeout(() => void poll(exportId, attempt + 1), 1000);
    } catch (err) {
      setBusy(false);
      setError(
        err instanceof ApiRequestError
          ? err.message
          : "Could not check the export.",
      );
    }
  }, []);

  async function download(status: ExportStatus) {
    if (!status.downloadUrl) return;
    // A plain link can't carry the bearer token, so the file is fetched and
    // handed to the browser as a blob.
    const res = await fetch(status.downloadUrl, {
      headers: {
        Authorization: `Bearer ${tokenStore.access ?? ""}`,
        ...(brandId ? { "x-brand-id": brandId } : {}),
      },
    });
    if (!res.ok) {
      setError("The export was generated but could not be downloaded.");
      return;
    }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = status.fileName ?? "narrative-report.pdf";
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }

  async function start() {
    if (!brandId) return;
    setBusy(true);
    setError(null);
    setState(null);
    try {
      const job = await api<{ exportId: string }>(
        `/brands/${brandId}/dashboard/export`,
        {
          method: "POST",
          body: { filters },
        },
      );
      await poll(job.exportId);
    } catch (err) {
      setBusy(false);
      setError(
        err instanceof ApiRequestError
          ? err.message
          : "Could not start the export.",
      );
    }
  }

  return (
    <>
      <button
        type="button"
        className="btn btn--primary btn--sm"
        onClick={() => void start()}
        disabled={busy || !brandId}
      >
        <Icon name="reports" />
        {busy
          ? state?.status === "rendering"
            ? "Rendering…"
            : "Preparing…"
          : "Export PDF"}
      </button>
      {error ? (
        <span className="badge badge--error" role="alert">
          {error}
        </span>
      ) : null}
    </>
  );
}
